import { useState, useRef, useEffect, useCallback } from 'react';

export const GetFormattedVHPlayerTime = (timeLeft) => {
  // Calculate hours, minutes, and seconds
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;

  return `${hours > 0 ? `${hours}:` : ''}${String(minutes).padStart(
    2,
    '0'
  )}:${String(seconds).padStart(2, '0')}`;
};

/**
 * A React hook for managing a countdown timer.
 * This mitigates set timeout drift by calculating to the nearest full second in the setTimeout
 * this does mean there may be 0-999ms worth of margin for error in the full timer but it keeps it
 * less error-prone than relying on subsequent setTimeouts of 1000
 * @param {number} [initialSeconds=0] - Initial countdown duration in seconds.
 * @param {Function} [onTimeoutFinished] - Optional callback to run when the timer finishes.
 * @returns {{
 *   startTimer: (durationInSeconds: number) => void,
 *   stopTimer: () => void,
 *   formattedTime: string
 * }}
 */
function useCountdownTimer(initialSeconds = 0, onTimeoutFinished = () => {}) {
  const [timeLeft, setTimeLeft] = useState(initialSeconds);

  /** @type {React.MutableRefObject<NodeJS.Timeout | null>} */
  const timerRef = useRef(null);

  /** @type {React.MutableRefObject<number | null>} */
  const endTimeRef = useRef(null); // Stores the exact end time in milliseconds

  /** @type {React.MutableRefObject<boolean>} */
  const timerStartedRef = useRef(false); // Prevents multiple starts of the timer

  /**
   * Updates the time left based on system time, preventing drift.
   */
  const tick = useCallback(() => {
    const now = Date.now();
    const remaining = Math.max(
      Math.floor((endTimeRef.current - now) / 1000),
      0
    );

    setTimeLeft(remaining);

    if (remaining > 0) {
      const delay = 1000 - (now % 1000); // Adjust for drift
      timerRef.current = setTimeout(tick, delay); // Schedule next tick
    } else {
      onTimeoutFinished(); // Call the callback when the timer reaches 0
    }
  }, [onTimeoutFinished]);

  /**
   * Starts the countdown timer.
   * @param {number} durationInSeconds - The duration of the countdown in seconds.
   */
  const startTimer = useCallback(
    (durationInSeconds) => {
      if (timerStartedRef.current) return; // Prevent restart if already started

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      const now = Date.now();
      endTimeRef.current = now + durationInSeconds * 1000; // Set the exact end time
      setTimeLeft(durationInSeconds);
      tick();

      timerStartedRef.current = true; // Mark timer as started
    },
    [tick]
  );

  /**
   * Stops the countdown timer.
   */
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    timerStartedRef.current = false; // Reset the timer start flag
  }, []);

  useEffect(() => {
    return () => stopTimer();
  }, [stopTimer]);

  // Format the time dynamically
  const formattedTime = GetFormattedVHPlayerTime(timeLeft);

  return { startTimer, stopTimer, formattedTime };
}

export default useCountdownTimer;
